import dynamic from 'next/dynamic'
import Router, { useRouter } from 'next/router'
import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useGridBlock } from '../../../contexts'
import { GridBlockContext } from '../../../contexts/providers'
import { storiesHe } from '../../../locales/stories.he'
import { useAdBlockStore } from '../../../store'
import { IStoriesBlock, IStory } from '../../../types/stories'
import { importWithRetry } from '../../../utils/importWithRetry'
import { holdPopState } from '../../../utils/popStateGuard'
import { startGeoProbe } from '../../Player/Helpers/useGeoManifestFetch'
import { IViewerPoint, ViewerEvent } from '../../VerticalViewer/types'
import { PlayerProvider } from '../Reels/PlayerContext'
import { PlayerContext } from '../../Player/Store/PlayerContext'
import { useNearViewport } from '../Reels/useNearViewport'
import { readNetwork } from '../Reels/network'
import { parseStories } from './storiesData'
import { kalturaPoster, posterWidth } from './storiesMedia'
import {
  parseShortsParam,
  SHORTS_LINK_PARAM,
  withoutShortsInPath,
  withoutShortsInState,
  withoutShortsParam,
} from './storiesLink'
import { focusQuietly, StoriesRail } from './StoriesRail'
import { RailSection } from './StoriesRail.style'
import {
  StoriesAdBlock,
  StoriesGeoBlock,
  StoriesLoadingScreen,
} from './StoriesScreens'
import {
  railOrder,
  resumePoint,
  seriesOrder,
  storyStatus,
  withFinished,
  withMuted,
  withPlayed,
  withPosition,
} from './storiesState'
import { StoriesEntry } from './storiesAnalytics'
import { useStoriesAnalytics } from './useStoriesAnalytics'
import { useStoriesState } from './useStoriesState'
import { useVisibleStories } from './useVisibleStories'
import { whenPageIdle } from './whenPageIdle'
import { isNativeApp } from '../../../client'

/**
 * The loading screen stands in the viewer's own place while its code is on
 * its way (next/dynamic's `loading`, which gets no props of its own): the
 * viewer then replaces it in one paint, with no page showing between them
 */
const ViewerLoadingContext = createContext<ComponentProps<
  typeof StoriesLoadingScreen
> | null>(null)
const ViewerLoading = () => {
  const screen = useContext(ViewerLoadingContext)
  return screen ? <StoriesLoadingScreen {...screen} /> : null
}
// the viewer's chunk brings its clip player: the import ends when both are here
const loadStoriesViewer = () => importWithRetry(() => import('./StoriesViewer'))
const StoriesViewer = dynamic(loadStoriesViewer, {
  ssr: false,
  loading: ViewerLoading,
})

/** the playing clip's second is written at most this often (and on every stop) */
const TIME_SAVE_MS = 1000
/** bubbles whose opening frame (an image) loads ahead, on any link but 2g */
const WARM_POSTERS = 4
/**
 * Live players kept ready for the bubbles on screen, so a tap plays at once:
 * on a fast link the clip each opens on and the one after it, on a slower one
 * (3g, a low-memory phone, a link that gives no hint) only the opening clip of
 * the first bubbles, none on 2g or with data saving. Each costs its first 10 s
 * of video (~1MB for a real vertical clip) and one Kaltura impression (its
 * analytics count a load).
 */
const WARM_STORIES_FAST = 10
const WARM_STORIES_SLOW = 2
const CLOSED_START: IViewerPoint = { clip: 0, time: 0 }

/** the history entry of the open player, over the page's own (`page`) */
interface IPlayerEntry {
  shortsViewer: number
  page: unknown
}
const isPlayerEntry = (state: unknown): state is IPlayerEntry =>
  typeof state === 'object' && state !== null && 'shortsViewer' in state
// steps back on their way, one for the page whatever block's player made
// them: a close taking its entry out, and one off an entry a closed player
// left. A player opened meanwhile puts its entry in after them
let closeStepBack = false
let strayStepBack = false

interface IOpen {
  /** the player's order when it opened (`seriesOrder`): "next" follows it */
  order: IStory[]
  /** index in `order` */
  group: number
  point: IViewerPoint
  /** the tap (Date.now()): a wait for the first clip counts from it */
  at: number
  entry: StoriesEntry
}

/** the frame an opening starts on: the url the viewer paints, so one download */
const openingPoster = ({ order, group, point }: IOpen) => {
  const cards = order[group]?.cards ?? []
  const card = cards[Math.min(Math.max(0, point.clip), cards.length - 1)]
  return card ? kalturaPoster(card.kalturaId, posterWidth()) : ''
}

const Stories = ({ data }: { data: IStoriesBlock }) => {
  const stories = useMemo(() => parseStories(data), [data])
  const { state, stateRef, save } = useStoriesState()
  const [open, setOpen] = useState<IOpen | null>(null)
  // each opening is a viewing of its own (the sound starts from the user's
  // choice again)
  const [openings, setOpenings] = useState(0)
  const openedId = useRef<string | null>(null)
  const adDecision = useAdBlockStore(s => s.decision)
  const sectionRef = useRef<HTMLElement>(null)

  // the viewer's players and ads report this block, also on pages that do
  // not give each block its own context (FaceLift and live pages)
  const block = useGridBlock()
  const blockContext = useMemo(
    () => ({ ...block, gridId: data.grid_id }),
    [block, data.grid_id]
  )

  const loadViewer = useCallback(() => {
    loadStoriesViewer().catch(() => undefined)
  }, [])

  const ordered = useMemo(() => railOrder(stories, state), [stories, state])
  // the player plays the block as a series (SHORTS-44), not in the rail's order
  const series = useMemo(() => seriesOrder(stories), [stories])
  const analytics = useStoriesAnalytics({
    stories,
    ordered,
    gridId: data.grid_id,
    stateRef,
    sectionRef,
  })

  const openAt = useCallback(
    (story: IStory, point: IViewerPoint, entry: StoriesEntry) => {
      const order = series
      const group = order.indexOf(story)
      if (group < 0) {
        return
      }
      openedId.current = story.id
      setOpenings(n => n + 1)
      setOpen({ order, group, point, at: Date.now(), entry })
      analytics.open(order, group, point, entry)
      loadViewer()
    },
    [series, loadViewer, analytics]
  )

  // the last input on the page: keys, or a tap or click (which draws no ring)
  const byKeys = useRef(false)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.altKey && !e.ctrlKey) {
        byKeys.current = true
      }
    }
    const onPointerDown = () => {
      byKeys.current = false
    }
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [])

  // closed: the focus goes back to the bubble that opened the viewer (Safari
  // does not focus a clicked button, and a shared link has no opener at all);
  // its ring shows only after keys
  useEffect(() => {
    const id = openedId.current
    if (open || id === null) {
      return
    }
    openedId.current = null
    const bubble = Array.from(
      sectionRef.current?.querySelectorAll<HTMLElement>('li[data-story]') ?? []
    )
      .find(li => li.dataset.story === id)
      ?.querySelector('button')
    if (!bubble) {
      return
    }
    if (byKeys.current) {
      bubble.focus({ preventScroll: true })
    } else {
      focusQuietly(bubble)
    }
  }, [open])

  // the viewer's and the player's code load once the row nears the screen,
  // after the page itself (the row is often at the top of the home page);
  // then the viewer waits closed, keeping the bubbles on screen ready
  const view = useNearViewport(sectionRef)
  const [warmLevel, setWarmLevel] = useState(0)
  useEffect(() => {
    if (!view.seen) {
      return undefined
    }
    return whenPageIdle(() => {
      // ahead of a tap only: a load that fails here is tried again by the
      // viewer itself when it is needed
      loadViewer()
      const { ahead } = readNetwork()
      setWarmLevel(ahead)
      // the frame each of the first bubbles opens on (its resume point), from
      // the url the viewer shows: a tap draws it at once, not a black stage
      if (ahead === 0) {
        return
      }
      const width = posterWidth()
      railOrder(stories, stateRef.current)
        .slice(0, WARM_POSTERS)
        .forEach(story => {
          const card = story.cards[resumePoint(story, stateRef.current).clip]
          if (card) {
            const image = new Image()
            image.src = kalturaPoster(card.kalturaId, width)
          }
        })
    })
  }, [view.seen, stories, stateRef, loadViewer])

  // the clips do not play outside Israel (D45): asked once the row is near
  // or a link opens it, and only a confirmed answer shows the geo screen; no
  // answer at all (no network) is not taken for abroad
  const [abroad, setAbroad] = useState(false)
  const geoWanted = view.seen || !!open
  useEffect(() => {
    if (!geoWanted) {
      return undefined
    }
    return startGeoProbe(result => setAbroad(result === 'Not Israel'))
  }, [geoWanted])

  // the viewer's groups: the series it opened on, kept until it closes
  const sequence = open ? open.order : series

  // the bubbles on screen, each at the point it opens on. They come once the
  // row was near the screen (warmLevel is set only then) and stay when the
  // page scrolls on: a return would load every clip again (Kaltura signs its
  // links per load, so nothing comes from the cache) and count each one in
  // Kaltura's statistics again. "On screen" is read inside the row, so a
  // scroll of the row itself still swaps them
  const warmOn = warmLevel > 0 && adDecision !== 'block' && !abroad
  const visible = useVisibleStories(sectionRef, ordered, warmOn)
  const warm = useMemo(() => {
    const fast = warmLevel >= 2
    return visible
      .slice(0, fast ? WARM_STORIES_FAST : WARM_STORIES_SLOW)
      .map(id => sequence.findIndex(story => story.id === id))
      .filter(group => group >= 0)
      .map(group => ({
        group,
        point: resumePoint(sequence[group], state),
        clips: fast ? 2 : 1,
      }))
  }, [visible, warmLevel, sequence, state])

  const statusOf = useCallback(
    (story: IStory) => storyStatus(story, stories, state),
    [stories, state]
  )

  const openStory = useCallback(
    (story: IStory) => {
      analytics.bubbleClick(story)
      openAt(story, resumePoint(story, stateRef.current), 'stories_rail')
    },
    [stateRef, openAt, analytics]
  )

  // ---- where the user is, saved so every page resumes on the same second
  const playing = useRef<{ id: string; clip: number; time: number } | null>(
    null
  )
  const lastTimeSave = useRef(0)

  const flushTime = useCallback(() => {
    const p = playing.current
    if (p) {
      save(s => withPosition(s, p.id, p.clip, p.time, Date.now()), false)
    }
  }, [save])

  const onClipStart = useCallback(
    (group: number, clip: number) => {
      const story = sequence[group]
      if (!story) {
        return
      }
      flushTime()
      analytics.clipStart(group, clip)
      // the clip it resumes on keeps its second until the player reports one
      const stored = stateRef.current.s[story.id]
      const time = stored && stored.c === clip ? stored.t : 0
      playing.current = { id: story.id, clip, time }
      save(s => withPosition(s, story.id, clip, time, Date.now()), false)
    },
    [sequence, stateRef, save, flushTime, analytics]
  )

  const onTime = useCallback(
    (group: number, clip: number, time: number) => {
      const story = sequence[group]
      if (!story) {
        return
      }
      playing.current = { id: story.id, clip, time }
      analytics.time(group, clip, time)
      const now = Date.now()
      if (now - lastTimeSave.current >= TIME_SAVE_MS) {
        lastTimeSave.current = now
        save(s => withPosition(s, story.id, clip, time, now), false)
      }
    },
    [sequence, save, analytics]
  )

  // a clip that played counts toward the story being watched; the analytics
  // hear the event as they do every other
  const onViewerEvent = useCallback(
    (event: ViewerEvent) => {
      if (event.type === 'clipPlaying') {
        const story = sequence[event.group]
        if (story) {
          save(s => withPlayed(s, story.id, event.clip, Date.now()), false)
        }
      }
      analytics.event(event)
    },
    [sequence, save, analytics]
  )

  const onGroupEnd = useCallback(
    (group: number) => {
      const story = sequence[group]
      if (story) {
        playing.current = null
        save(s => withFinished(s, story.id, Date.now()), false)
      }
    },
    [sequence, save]
  )

  const closeWith = useCallback(
    (method: 'x' | 'browser_back') => {
      // the viewer reports how it closed; the screens before it close by
      // their X; Back is seen here
      analytics.close(method)
      flushTime()
      playing.current = null
      // the rail redraws now: colours and order follow what was just watched
      save(s => s, true)
      setOpen(null)
    },
    [flushTime, save, analytics]
  )
  const onClose = useCallback(() => closeWith('x'), [closeWith])
  const closeWithRef = useRef(closeWith)
  closeWithRef.current = closeWith

  // ---- Back closes the player (and the screens before it), the page stays:
  // while it is open there is a history entry of its own, and a close any
  // other way takes it out again, so the Back after it leaves the page as
  // before
  const leavingPage = useRef(false)
  useEffect(
    () => () => {
      leavingPage.current = true
    },
    []
  )
  const isOpen = open !== null
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    const id = Date.now()
    const isOurs = (state: unknown) =>
      isPlayerEntry(state) && state.shortsViewer === id
    let page: unknown = null
    let release: (() => void) | null = null
    let pushed = false
    let closed = false
    const onPop = () => {
      closeStepBack = false
      // forward onto our entry again: nothing to do
      if (isOurs(window.history.state)) {
        return
      }
      window.removeEventListener('popstate', onPop)
      // after every listener of this step (Next's too) had its turn
      setTimeout(() => release?.(), 0)
      if (!closed) {
        closed = true
        closeWithRef.current('browser_back')
      }
    }
    // a move to another page while open: Next pushes over our entry, so it
    // turns back into this page's own, and Back from there returns here
    const onLeave = () => {
      Router.events.off('beforeHistoryChange', onLeave)
      if (closed) {
        return
      }
      closed = true
      window.removeEventListener('popstate', onPop)
      release?.()
      if (isOurs(window.history.state)) {
        window.history.replaceState(page, '')
      }
    }
    const start = () => {
      window.removeEventListener('popstate', start)
      if (closed) {
        return
      }
      const current: unknown = window.history.state
      // an entry an earlier opening left (Forward onto it): ours takes it
      const left = isPlayerEntry(current)
      page = left ? current.page : current
      const below = (page as { key?: unknown } | null)?.key
      // the step back to the page's own entry is ours, not a navigation
      release = holdPopState(key => typeof below === 'string' && key === below)
      const entry: IPlayerEntry = { shortsViewer: id, page }
      if (left) {
        window.history.replaceState(entry, '')
      } else {
        window.history.pushState(entry, '')
      }
      pushed = true
      window.addEventListener('popstate', onPop)
      Router.events.on('beforeHistoryChange', onLeave)
    }
    // closed and opened again in one go (or Forward onto a left entry just
    // before): ours goes in after that step back
    if (closeStepBack || strayStepBack) {
      window.addEventListener('popstate', start)
    } else {
      start()
    }
    return () => {
      window.removeEventListener('popstate', start)
      Router.events.off('beforeHistoryChange', onLeave)
      if (closed) {
        return
      }
      closed = true
      if (!pushed) {
        return
      }
      // closed another way (X, Esc, the end): our entry goes, and the step
      // back that takes it out is caught by onPop above
      if (!leavingPage.current && isOurs(window.history.state)) {
        closeStepBack = true
        window.history.back()
        return
      }
      window.removeEventListener('popstate', onPop)
      release?.()
    }
  }, [isOpen])

  // the page's auto refresh waits while the player is open (as for Reels): an
  // ad or a turn between episodes plays with no Kaltura player playing, and
  // the refresh would close the player then
  const { setVideoPlaying } = useContext(PlayerContext)
  useEffect(() => {
    setVideoPlaying(isOpen)
    return () => setVideoPlaying(false)
  }, [isOpen, setVideoPlaying])

  // ---- Forward onto the entry a closed player left: the user is put back on
  // the page's own entry (Next stays out of the step), so no entry Next
  // ignores is ever the current one (a Back or a link from there would leave
  // the address and the page apart)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  useEffect(() => {
    const onStray = () => {
      const state: unknown = window.history.state
      if (
        isOpenRef.current ||
        closeStepBack ||
        strayStepBack ||
        !isPlayerEntry(state)
      ) {
        return
      }
      const page = state.page as { key?: unknown; as?: unknown } | null
      const below = page?.key
      // the step back to the page the router shows is ours; to another
      // page's entry (reached by a jump in the history) it is the router's
      const onThisPage =
        typeof page?.as === 'string' &&
        withoutShortsInPath(page.as) === withoutShortsInPath(Router.asPath)
      const release = onThisPage
        ? holdPopState(key => typeof below === 'string' && key === below)
        : null
      strayStepBack = true
      const landed = () => {
        window.removeEventListener('popstate', landed)
        strayStepBack = false
        // after every listener of this step (Next's too) had its turn
        setTimeout(() => release?.(), 0)
      }
      window.addEventListener('popstate', landed)
      window.history.back()
    }
    window.addEventListener('popstate', onStray)
    return () => window.removeEventListener('popstate', onStray)
  }, [])

  // closing the tab or leaving the page mid-clip keeps the second too
  useEffect(() => {
    if (!open) {
      return
    }
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        flushTime()
      }
    }
    window.addEventListener('pagehide', flushTime)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      window.removeEventListener('pagehide', flushTime)
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [open, flushTime])

  const groupStart = useCallback(
    (group: number) =>
      sequence[group]
        ? resumePoint(sequence[group], stateRef.current)
        : { clip: 0, time: 0 },
    [sequence, stateRef]
  )

  const onMuteToggle = useCallback(
    (muted: boolean) => {
      analytics.mute(muted)
      save(s => withMuted(s, muted), false)
    },
    [save, analytics]
  )

  // the wall shown instead of the viewer (an ad blocker): once per opening
  const wallShown = !!open && adDecision === 'block'
  useEffect(() => {
    if (wallShown) {
      analytics.adblockWall()
    }
  }, [wallShown, analytics])

  // what the loading screen draws while the viewer's code is on its way
  const loadingScreen = useMemo(
    () =>
      open ? { onClose, poster: openingPoster(open), since: open.at } : null,
    [open, onClose]
  )

  // a shared link (?shorts=<part id>) opens the episode at that part once.
  // Only after the router is ready: it puts the address it hydrated with back
  const { isReady } = useRouter()
  const linkHandled = useRef(false)
  useEffect(() => {
    if (!isReady || linkHandled.current || stories.length === 0) {
      return
    }
    const value = new URLSearchParams(window.location.search).get(
      SHORTS_LINK_PARAM
    )
    if (value === null) {
      return
    }
    const cardId = parseShortsParam(value)
    const story = cardId
      ? stories.find(candidate =>
          candidate.cards.some(card => card.id === cardId)
        )
      : undefined
    // a part of another block on the page is that block's to open; one no
    // block holds (an episode gone from the row, a broken or old link) is
    // reported once, by the first row, after every block had its turn
    if (!story) {
      const timer = setTimeout(() => {
        const first = document.querySelector('section[data-shorts]')
        const params = new URLSearchParams(window.location.search)
        if (
          first !== sectionRef.current ||
          params.get(SHORTS_LINK_PARAM) !== value
        ) {
          return
        }
        linkHandled.current = true
        window.history.replaceState(
          withoutShortsInState(window.history.state),
          '',
          withoutShortsParam(window.location.href)
        )
        sectionRef.current?.scrollIntoView({ block: 'center' })
        analytics.deeplink('card_not_found')
      })
      return () => clearTimeout(timer)
    }
    linkHandled.current = true
    const clip = story.cards.findIndex(card => card.id === cardId)
    window.history.replaceState(
      withoutShortsInState(window.history.state),
      '',
      withoutShortsParam(window.location.href)
    )
    sectionRef.current?.scrollIntoView({ block: 'center' })
    // the link is reported before the opening it leads to
    analytics.deeplink('success', story, story.cards[clip])
    openAt(story, { clip, time: 0 }, 'deeplink')
  }, [isReady, stories, openAt, analytics])

  if (stories.length === 0) {
    return null
  }

  return (
    <RailSection
      ref={sectionRef}
      aria-label={storiesHe.rail}
      data-shorts={data.grid_id}
    >
      <StoriesRail stories={ordered} statusOf={statusOf} onOpen={openStory} />
      {(open || warmLevel > 0) && (
        <GridBlockContext.Provider value={blockContext}>
          <PlayerProvider
            componentType='shorts'
            startMuted={stateRef.current.muted ?? false}
            onMuteToggle={onMuteToggle}
            session={openings}
          >
            {open && abroad && <StoriesGeoBlock onClose={onClose} />}
            {open && !abroad && adDecision === 'block' && (
              <StoriesAdBlock onClose={onClose} />
            )}
            {!abroad && adDecision !== 'block' && (
              <ViewerLoadingContext.Provider value={loadingScreen}>
                <StoriesViewer
                  stories={sequence}
                  statusOf={statusOf}
                  open={!!open}
                  warm={warmOn ? warm : undefined}
                  startGroup={open?.group ?? 0}
                  start={open?.point ?? CLOSED_START}
                  openedAt={open?.at}
                  entry={open?.entry ?? 'stories_rail'}
                  groupStart={groupStart}
                  gridId={data.grid_id}
                  adConfig={data.ads}
                  onClose={onClose}
                  onClipStart={onClipStart}
                  onTime={onTime}
                  onGroupEnd={onGroupEnd}
                  onEvent={onViewerEvent}
                  onShared={analytics.share}
                />
              </ViewerLoadingContext.Provider>
            )}
          </PlayerProvider>
        </GridBlockContext.Provider>
      )}
    </RailSection>
  )
}

const StoriesHandler = ({ data }: { data: IStoriesBlock }) => {
  const [hideInApp, setHideInApp] = useState(false)

  useEffect(() => {
    if (isNativeApp() && data.hideInApp === true) {
      setHideInApp(true)
    }
  }, [data.hideInApp])

  if (hideInApp) {
    return null
  }

  return <Stories data={data} />
}
export default StoriesHandler
